import { useEffect, useState } from 'react';
import { Modal, TextInput, Button, Group, Stack, Alert, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { CustomerSelectComponent } from '../components/customer_select_component';
import { CompanyAuthorizedSignatories, Customer } from '../types';

interface Props {
    opened: boolean;
    onClose: () => void;
    onSubmit: (
        data: Pick<CompanyAuthorizedSignatories, 'id' | 'role_within_company'>,
        editingOriginal: CompanyAuthorizedSignatories | null,
    ) => Promise<boolean>;
    editing: CompanyAuthorizedSignatories | null;
    loading: boolean;
    error: string | null;
}

export const SignatoryModal = ({ opened, onClose, onSubmit, editing, loading, error }: Props) => {
    const [customerData, setCustomerData] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

    const form = useForm({
        initialValues: {
            role_within_company: '',
        },
        validate: {
            role_within_company: (v) => (!v ? 'Required' : null),
        },
    });

    useEffect(() => {
        if (opened) {
            if (editing) {
                form.setValues({ role_within_company: editing.role_within_company });
            } else {
                form.reset();
                setSelectedCustomer(undefined);
                setCustomerData([]);
            }
        }
    }, [editing, opened]);

    const handleSubmit = async (values: typeof form.values) => {
        if (!editing && !selectedCustomer?.id) {
            form.setErrors({ role_within_company: 'Please select a customer' });
            return;
        }

        const success = await onSubmit(
            {
                id: editing ? editing.id : selectedCustomer!.id!,
                role_within_company: values.role_within_company,
            },
            editing,
        );
        if (success) onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={editing ? 'Edit Signatory' : 'Add Signatory'}
            size="md"
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="sm">
                    {error && <Alert color="red">{error}</Alert>}

                    {editing ? (
                        <TextInput
                            label="Customer ID"
                            value={editing.id}
                            disabled
                        />
                    ) : (
                        <CustomerSelectComponent
                            setCustomerData={setCustomerData}
                            customerData={customerData as any}
                            textProps={{
                                label: 'Customer',
                                placeholder: 'Search by Civil ID, Other ID or Commercial Registry',
                            }}
                            handleSubmit={(customerId) => {
                                const found = customerData.find((c) => c.id === customerId);
                                setSelectedCustomer(found);
                            }}
                        />
                    )}

                    {selectedCustomer && !editing && (
                        <Stack gap={2}>
                            <Text size="sm" c="dimmed">Selected: <strong>{selectedCustomer.name_en || selectedCustomer.name_ar}</strong></Text>
                            <Text size="sm" c="dimmed">Civil ID: <strong>{selectedCustomer.civilID}</strong></Text>
                        </Stack>
                    )}

                    <TextInput
                        label="Role Within Company"
                        placeholder="e.g. Director, CEO"
                        {...form.getInputProps('role_within_company')}
                    />

                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button type="submit" loading={loading}>
                            {editing ? 'Update' : 'Add'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
};